import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

export interface InventoryAuditorItemRow {
  id: number;
  upc: string;
  name: string | null;
  description?: string | null;
  imageUrl?: string | null;
  imageS3Key?: string | null;
  scanContext?: string | null;
  uomPackType?: string | null;
  size?: string | null;
  catalogOrigin?: 'pdi' | 'scanned' | null;
  /** PDI item / slot number (leading zeros stripped). */
  pdiNumber?: string | null;
  createdAt?: string;
  updatedAt?: string;
  deleted?: boolean;
}

export interface InventoryAuditorBalanceRow {
  balanceId: number;
  quantity: number;
  inventoryItem: InventoryAuditorItemRow;
}

export interface InventoryAuditorCountSnapshotRow {
  inventoryItemId: number;
  upc: string;
  name: string | null;
  uomPackType: string;
  size: string;
  imageUrl: string | null;
  quantity: number;
  countDate: string;
  updatedAt: string;
}

export async function queryInventoryAuditorBalancesByLocation(params: {
  locationId: number;
  skip?: number;
  take?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: string;
}): Promise<{ data: InventoryAuditorBalanceRow[]; total: number; countDate?: string; countCalendarTimeZone?: string }> {
  const response = await httpClient.post('api/inventory-auditor/inventory/balance/by-location', {
    locationId: params.locationId,
    skip: params.skip ?? 0,
    take: params.take ?? 5000,
    search: params.search ?? '',
    orderBy: params.orderBy ?? 'upc',
    orderDirection: params.orderDirection ?? 'ASC',
  });
  const d = response.data as {
    data?: InventoryAuditorBalanceRow[];
    total?: number;
    countDate?: string;
    countCalendarTimeZone?: string;
  };
  return {
    data: d.data ?? [],
    total: d.total ?? 0,
    countDate: d.countDate,
    countCalendarTimeZone: d.countCalendarTimeZone,
  };
}

export async function queryInventoryAuditorLocationCatalog(params: {
  locationId: number;
  skip?: number;
  take?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: string;
}): Promise<{
  data: Array<InventoryAuditorItemRow & { quantityAtLocation: number }>;
  total: number;
  countDate?: string;
}> {
  const response = await httpClient.post('api/inventory-auditor/inventory/balance/location-catalog', {
    locationId: params.locationId,
    skip: params.skip ?? 0,
    take: params.take ?? 25,
    search: params.search ?? '',
    orderBy: params.orderBy ?? 'name',
    orderDirection: params.orderDirection ?? 'ASC',
  });
  const d = response.data as {
    data?: Array<InventoryAuditorItemRow & { quantityAtLocation: number }>;
    total?: number;
    countDate?: string;
  };
  return { data: d.data ?? [], total: d.total ?? 0, countDate: d.countDate };
}

/** All IA catalog items (every UPC ever added), not filtered by location or quantity. */
export async function queryInventoryAuditorGlobalCatalog(params: {
  skip?: number;
  take?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: string;
  showDeleted?: boolean;
}): Promise<{ data: InventoryAuditorItemRow[]; total: number }> {
  const response = await httpClient.post('api/inventory-auditor/inventory/query', {
    skip: params.skip ?? 0,
    take: params.take ?? 50,
    search: params.search ?? '',
    orderBy: params.orderBy ?? 'upc',
    orderDirection: params.orderDirection ?? 'ASC',
    showDeleted: params.showDeleted === true,
  });
  const d = response.data as { data?: InventoryAuditorItemRow[]; total?: number };
  return { data: d.data ?? [], total: d.total ?? 0 };
}

export type InventoryAuditorUpcVariant = {
  inventoryItemId: number;
  primaryUpc: string;
  retailUpc?: string;
  name: string | null;
  uomPackType: string;
  size: string;
  imageUrl: string | null;
  catalogOrigin?: 'pdi' | 'scanned';
};

export async function resolveInventoryAuditorUpc(
  upc: string,
  opts?: {
    autoCreateInCatalog?: boolean;
    uomPackType?: string;
    size?: string;
    imageS3Key?: string;
    scanContext?: string;
  },
): Promise<{
  found: boolean;
  inventoryItemId?: number;
  primaryUpc?: string;
  retailUpc?: string;
  name?: string | null;
  imageUrl?: string | null;
  imageS3Key?: string | null;
  scanContext?: string | null;
  uomPackType?: string | null;
  size?: string | null;
  catalogOrigin?: 'pdi' | 'scanned';
  catalogIncomplete?: boolean;
  requiresCatalogAttributes?: boolean;
  requiresDisambiguation?: boolean;
  notInCatalog?: boolean;
  variants?: InventoryAuditorUpcVariant[];
}> {
  const response = await httpClient.post('api/inventory-auditor/inventory/resolve-upc-item', {
    upc,
    autoCreateInCatalog: opts?.autoCreateInCatalog === true,
    ...(opts?.uomPackType != null && opts.uomPackType !== ''
      ? { uomPackType: opts.uomPackType }
      : {}),
    ...(opts?.size != null && opts.size !== '' ? { size: opts.size } : {}),
    ...(opts?.imageS3Key != null && opts.imageS3Key !== '' ? { imageS3Key: opts.imageS3Key } : {}),
    ...(opts?.scanContext != null && opts.scanContext !== '' ? { scanContext: opts.scanContext } : {}),
  });
  return response.data as any;
}

export async function getInventoryAuditorImageUploadUrl(
  fileExtension: string,
  contentType: string,
): Promise<{ uploadUrl: string; s3Key: string }> {
  const response = await httpClient.get<{ uploadUrl: string; s3Key: string }>(
    'api/inventory-auditor/inventory/image-upload-url',
    { fileExtension, contentType } as Record<string, string>,
  );
  return response.data as unknown as { uploadUrl: string; s3Key: string };
}

export async function uploadInventoryAuditorImageToPresignedUrl(
  uploadUrl: string,
  file: Blob,
  contentType: string,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status})`);
  }
}

export async function applyInventoryAuditorSiteVisit(payload: {
  locationId: number;
  lines: Array<{ inventoryItemId: number; quantity: number }>;
}): Promise<{ countDate: string }> {
  const response = await httpClient.post('api/inventory-auditor/inventory/balance/site-visit-apply', payload);
  const d = response.data as { countDate?: string };
  const countDate = d.countDate;
  if (countDate == null || countDate === '') {
    throw new Error('Apply response missing countDate');
  }
  return { countDate };
}

export async function syncInventoryAuditorSiteVisit(payload: {
  locationId: number;
  since: string;
  countDate?: string;
}): Promise<{
  countDate: string;
  lines: Array<{ inventoryItemId: number; quantity: number; updatedAt: string }>;
}> {
  const response = await httpClient.post('api/inventory-auditor/inventory/balance/site-visit-sync', payload);
  return response.data as {
    countDate: string;
    lines: Array<{ inventoryItemId: number; quantity: number; updatedAt: string }>;
  };
}

export async function fetchInventoryAuditorCountSnapshotDates(locationId: number): Promise<{ dates: string[] }> {
  const response = await httpClient.post('api/inventory-auditor/inventory/balance/count-snapshot/dates', {
    locationId,
  });
  const d = response.data as { dates?: unknown };
  const dates = Array.isArray(d.dates) ? (d.dates as string[]) : [];
  return { dates: dates.filter((x) => typeof x === 'string').map((x) => x.trim()) };
}

export type InventoryAuditorCountSnapshotRecentRow = {
  locationId: number;
  locationName: string;
  countDate: string;
  lineCount: number;
};

export async function fetchInventoryAuditorCountSnapshotRecent(params?: {
  skip?: number;
  take?: number;
}): Promise<{ data: InventoryAuditorCountSnapshotRecentRow[]; total: number }> {
  const response = await httpClient.post('api/inventory-auditor/inventory/balance/count-snapshot/recent', {
    skip: params?.skip ?? 0,
    take: params?.take ?? 20,
  });
  const d = response.data as { data?: InventoryAuditorCountSnapshotRecentRow[]; total?: number };
  return { data: d.data ?? [], total: d.total ?? 0 };
}

export async function fetchInventoryAuditorCountSnapshotQuery(params: {
  locationId: number;
  countDate: string;
  skip?: number;
  take?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: string;
}): Promise<{
  data: InventoryAuditorCountSnapshotRow[];
  total: number;
  countDate: string;
  location: { id: number; name?: string };
}> {
  const response = await httpClient.post('api/inventory-auditor/inventory/balance/count-snapshot/query', {
    locationId: params.locationId,
    countDate: params.countDate,
    skip: params.skip ?? 0,
    take: params.take ?? 500,
    search: params.search ?? '',
    orderBy: params.orderBy ?? 'upc',
    orderDirection: params.orderDirection ?? 'ASC',
  });
  const raw = response.data as {
    data?: InventoryAuditorCountSnapshotRow[];
    total?: number;
    countDate?: string;
    location?: { id: number; name?: string };
  };
  return {
    data: raw.data ?? [],
    total: raw.total ?? 0,
    countDate: raw.countDate ?? params.countDate,
    location: raw.location ?? { id: params.locationId },
  };
}

export async function updateInventoryAuditorItem(payload: {
  id: number;
  name?: string | null;
  description?: string | null;
  scanContext?: string | null;
  uomPackType?: string;
  size?: string;
  deleted?: boolean;
}): Promise<InventoryAuditorItemRow> {
  const response = await httpClient.post('api/inventory-auditor/inventory/update', payload);
  const raw = response.data as { item?: InventoryAuditorItemRow };
  if (!raw.item) {
    throw new Error('Inventory item not found');
  }
  return raw.item;
}

export async function fetchInventoryAuditorItemDetails(id: number): Promise<InventoryAuditorItemRow> {
  const response = await httpClient.post('api/inventory-auditor/inventory/details', { id });
  const raw = response.data as { item?: InventoryAuditorItemRow };
  const item = raw.item;
  if (!item) {
    throw new Error('Inventory item not found');
  }
  return item;
}

export async function enrichInventoryAuditorItemFromOpenFoodFacts(id: number): Promise<{
  foundOff: boolean;
  updated: boolean;
  item: InventoryAuditorItemRow;
}> {
  const response = await httpClient.post('api/inventory-auditor/inventory/enrich-from-open-food-facts', { id });
  return response.data as { foundOff: boolean; updated: boolean; item: InventoryAuditorItemRow };
}
